import { Context } from '../context/Context';
import { FullscreenQuad } from './FullscreenQuad';
import { RenderTexture } from './RenderTarget';

/**
 * Always-on present pass — samples the HDR scene FBO and writes the
 * ACES-tonemapped result to either the FXAA input FBO or the canvas. Decoupled
 * from bloom so every (bloom on/off × taa on/off × fxaa on/off) preset combo
 * produces a properly tonemapped image.
 *
 * Stateless: no internal FBOs / textures. Inputs are passed in per frame.
 */
export class FinalPresentPass {
  /** Linear-to-display exposure. Picked up from {@link GraphicsSettings.exposure}. */
  public exposure = 1.0;

  public init(_ctx: Context): void {
    // Nothing to allocate — shader+quad are shared, FBOs come from the caller.
  }

  public render(
    ctx: Context,
    quad: FullscreenQuad,
    hdrScene: RenderTexture,
    outputBuffer: WebGLFramebuffer | null,
  ): void {
    const gl = ctx.gl;

    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, outputBuffer);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);

    const shader = ctx.engine.shaderManager.getFinalPresentShader();
    ctx.setShader(shader);

    shader.uniformsContainer.setHdrScene(hdrScene).setExposure(this.exposure);

    shader.validate();
    quad.draw(gl);
  }

  public destroy(_gl: WebGL2RenderingContext): void {
    // No GL resources owned.
  }
}
